<script setup>
import { onMounted, ref, watch } from 'vue';
import emailjs from 'emailjs-com';

const name = ref('');
const email = ref('');
const message = ref('');
const gdprConsent = ref(false);

const serviceID = process.env.VUE_APP_EMAILJS_SERVICE_ID;
const templateID = process.env.VUE_APP_EMAILJS_TEMPLATE_ID;
const publicKey = process.env.VUE_APP_EMAILJS_PUBLIC_KEY;

const successMessage = ref('');
const nameError = ref('');
const emailError = ref('');
const messageError = ref('');
const gdprError = ref('');

const validateEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

onMounted(() => emailjs.init(publicKey));

const handleSubmit = (e) => {
  e.preventDefault();
  nameError.value = '';
  emailError.value = '';
  messageError.value = '';
  gdprError.value = '';

  if (!name.value) nameError.value = 'Name is required.';
  if (!email.value || !validateEmail(email.value)) emailError.value = 'Please enter a valid email address.';
  if (!message.value) messageError.value = 'Message is required.';
  if (!gdprConsent.value) gdprError.value = 'You must agree to the GDPR terms.';

  if (!nameError.value && !emailError.value && !messageError.value && gdprConsent.value) {
    emailjs.send(serviceID, templateID, {
      user_name: name.value,
      user_email: email.value,
      message: message.value,
    }).then(() => {
      name.value = '';
      email.value = '';
      message.value = '';
      gdprConsent.value = false;
      successMessage.value = 'Great, I have received your email!';
    }, (error) => {
      console.log('FAILED...', error.text);
      alert('Failed to send your message. Please try again later.');
    });
  }
};

watch([name, email, message], ([n, e, m]) => {
  if (n) nameError.value = '';
  if (e && validateEmail(e)) emailError.value = '';
  if (m) messageError.value = '';
});
</script>

<template>
  <section class="contact-section">
    <div class="blob blob-1" />
    <div class="blob blob-2" />

    <div class="contact-header">
      <span class="label">Get in touch</span>
      <h2>Say Hello</h2>
    </div>

    <div class="contact-layout">

      <!-- Left: tagline -->
      <div class="contact-left">
        <p class="tagline">Wanna talk and see what we can do together?</p>
        <a href="mailto:naeliasalas@gmail.com" class="email-link">naeliasalas@gmail.com</a>
      </div>

      <!-- Right: form -->
      <div class="contact-right">
        <form novalidate @submit="handleSubmit">

          <div class="form-group">
            <input v-model="name" type="text" placeholder="Your name" :class="{ error: nameError }" />
            <span v-if="nameError" class="error-message">{{ nameError }}</span>
          </div>

          <div class="form-group">
            <input v-model="email" type="email" placeholder="Your email" :class="{ error: emailError }" />
            <span v-if="emailError" class="error-message">{{ emailError }}</span>
          </div>

          <div class="form-group">
            <textarea v-model="message" placeholder="Your message" :class="{ error: messageError }" />
            <span v-if="messageError" class="error-message">{{ messageError }}</span>
          </div>

          <div class="form-group gdpr-group">
            <label class="gdpr-label">
              <input type="checkbox" v-model="gdprConsent" class="gdpr-checkbox" />
              <span>I agree to the
                <router-link to="/privacy-policy">GDPR & privacy terms</router-link>
              </span>
            </label>
            <span v-if="gdprError" class="error-message">{{ gdprError }}</span>
          </div>

          <button type="submit" class="submit-btn">Send message →</button>

          <div v-if="successMessage" class="success-message">
            ✅ {{ successMessage }}
          </div>

        </form>
      </div>
    </div>
  </section>
</template>

<style scoped>
.contact-section {
  position: relative;
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto 100px;
  overflow: hidden;
}

/* Blobs */
.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.blob-1 {
  width: 400px; height: 400px;
  background: rgba(102, 126, 234, 0.15);
  bottom: -100px; left: -100px;
}
.blob-2 {
  width: 300px; height: 300px;
  background: rgba(118, 75, 162, 0.15);
  top: 0; right: -100px;
}

/* Header */
.contact-header {
  position: relative;
  z-index: 1;
  text-align: center;
  margin-bottom: 4rem;
}

.label {
  font-size: 0.8rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #667eea;
  font-weight: 600;
}

h2 {
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  letter-spacing: -2px;
  background: linear-gradient(-45deg, #667eea, #764ba2, #a8c0ff, #c471ed);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 8s ease infinite;
  margin-top: 0.5rem;
}

@keyframes gradientShift {
  0%   { background-position: 0% 50% }
  50%  { background-position: 100% 50% }
  100% { background-position: 0% 50% }
}

/* Layout */
.contact-layout {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

/* Left */
.contact-left {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.tagline {
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  color: white;
  line-height: 1.2;
  letter-spacing: -1px;
}

.email-link {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.4);
  text-decoration: none;
  letter-spacing: 1px;
  transition: color 0.3s;
}

.email-link:hover { color: #667eea }

/* Form */
.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1.2rem;
}

input, textarea {
  width: 100%;
  padding: 1rem 1.2rem;
  font-size: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  color: white;
  outline: none;
  transition: border-color 0.3s;
  backdrop-filter: blur(10px);
}

input::placeholder, textarea::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

input:focus, textarea:focus {
  border-color: #667eea;
}

input.error, textarea.error {
  border-color: rgba(255, 100, 100, 0.5);
}

textarea { min-height: 130px; resize: vertical }

/* GDPR */
.gdpr-group { gap: 0.5rem }

.gdpr-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
}

.gdpr-checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: #667eea;
}

.gdpr-label a {
  color: #667eea;
  text-decoration: none;
}

.gdpr-label a:hover { text-decoration: underline }

/* Errors */
.error-message {
  color: rgba(255, 120, 120, 0.9);
  font-size: 0.8rem;
  margin-top: 4px;
}

/* Button */
.submit-btn {
  width: 100%;
  padding: 1rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  transition: all 0.3s;
  margin-top: 0.5rem;
}

.submit-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 40px rgba(102, 126, 234, 0.4);
}

/* Success */
.success-message {
  margin-top: 1rem;
  color: rgba(120, 220, 120, 0.9);
  font-size: 0.9rem;
  text-align: center;
}

@media (max-width: 768px) {
  .contact-layout { grid-template-columns: 1fr }
  .tagline { font-size: 1.8rem }
  .contact-left { text-align: center; align-items: center }
}
</style>